/**
 * TEMP-H-009: Music Event Card Component
 * 
 * Specialized renderer for music stage events with waveform preview,
 * artifact links (master/mastered/sidechained), and metadata display.
 */

import React, { useState, memo, Suspense } from 'react';
import { 
  Music, 
  Play, 
  Pause, 
  Volume2, 
  Clock, 
  Hash,
  Download,
  ExternalLink,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import type { TimelineEvent, ArtifactRef } from '@/types/execution-events';
import { getAudioTypeColors, getStageColors } from '@/lib/stage-colors';

// CSS-in-JS styles for hover interactions using tokens
const audioActionButtonStyles = `
  .audio-action-button:hover {
    background-color: var(--bg-interactive-hover) !important;
  }
  
  .audio-action-button:focus-visible {
    background-color: var(--bg-interactive-active) !important;
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('audio-action-button-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'audio-action-button-styles';
  styleElement.textContent = audioActionButtonStyles;
  document.head.appendChild(styleElement);
}

interface MusicEventCardProps {
  event: TimelineEvent;
  compact?: boolean;
  showArtifacts?: boolean;
}

// Lazy-loaded waveform component
const LazyWaveform = React.lazy(() => import('./WaveformVisualization'));

interface AudioArtifactProps {
  artifact: ArtifactRef;
  isPlaying?: boolean;
  onPlayToggle?: () => void;
}

const AudioArtifact: React.FC<AudioArtifactProps> = ({ artifact, isPlaying, onPlayToggle }) => {
  const audioColors = getAudioTypeColors(artifact.audioType);

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return '--';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)}MB`;
  };

  return (
    <div 
      className="flex items-center rounded-lg border"
      style={{
        gap: 'var(--space-4)',
        padding: 'var(--space-4)',
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-default)'
      }}
    >
      {/* Waveform Preview (if available) */}
      {artifact.waveformUri && (
        <div className="w-16 h-8 flex-shrink-0">
          <Suspense fallback={
            <div 
              className="w-full h-full rounded animate-pulse"
              style={{ backgroundColor: 'var(--bg-subtle)' }}
            />
          }>
            <LazyWaveform
              waveformUri={artifact.waveformUri}
              isPlaying={isPlaying}
              width={64}
              height={32}
            />
          </Suspense>
        </div>
      )}

      {/* Audio Details */}
      <div className="flex-1 min-w-0">
        <div 
          className="flex items-center mb-1"
          style={{ gap: 'var(--space-2)' }}
        >
          <h5 
            className="font-medium truncate"
            style={{ 
              fontSize: 'var(--font-size-sm)',
              color: 'var(--fg-primary)'
            }}
          >
            {artifact.name}
          </h5>
          {artifact.audioType && (
            <span 
              className="rounded-full"
              style={{
                padding: 'var(--space-1) var(--space-2)',
                fontSize: 'var(--font-size-xs)',
                backgroundColor: audioColors.bgSubtle,
                color: audioColors.text
              }}
            >
              {artifact.audioType}
            </span>
          )}
        </div>
        
        <div 
          className="flex items-center"
          style={{ 
            gap: 'var(--space-4)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--fg-muted)'
          }}
        >
          <span 
            className="flex items-center"
            style={{ gap: 'var(--space-1)' }}
          >
            <Clock style={{ width: 'var(--space-3)', height: 'var(--space-3)' }} />
            {formatDuration(artifact.duration)}
          </span>
          <span>{formatSize(artifact.size)}</span>
          {artifact.musicMetadata?.bpm && (
            <span>{artifact.musicMetadata.bpm} BPM</span>
          )}
          {artifact.musicMetadata?.key && (
            <span>Key: {artifact.musicMetadata.key}</span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {artifact.publicUrl && (
          <button
            onClick={onPlayToggle}
            className="audio-action-button"
            style={{
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-lg)',
              transition: `background-color var(--motion-duration-fast)`,
              backgroundColor: 'transparent'
            }}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause 
                style={{
                  width: 'var(--space-5)',
                  height: 'var(--space-5)',
                  color: 'var(--accent)'
                }}
              />
            ) : (
              <Play 
                style={{
                  width: 'var(--space-5)',
                  height: 'var(--space-5)',
                  color: 'var(--accent)'
                }}
              />
            )}
          </button>
        )}
        
        {artifact.publicUrl && (
          <a
            href={artifact.publicUrl}
            download={artifact.name}
            className="audio-action-button"
            style={{
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-lg)',
              transition: `background-color var(--motion-duration-fast)`,
              backgroundColor: 'transparent'
            }}
            title="Download"
          >
            <Download 
              style={{
                width: 'var(--space-5)',
                height: 'var(--space-5)',
                color: 'var(--fg-muted)'
              }}
            />
          </a>
        )}
        
        {artifact.publicUrl && (
          <a
            href={artifact.publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="audio-action-button"
            style={{
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-lg)',
              transition: `background-color var(--motion-duration-fast)`,
              backgroundColor: 'transparent'
            }}
            title="Open in new tab"
          >
            <ExternalLink 
              style={{
                width: 'var(--space-5)',
                height: 'var(--space-5)',
                color: 'var(--fg-muted)'
              }}
            />
          </a>
        )}
      </div>
    </div>
  );
};

export const MusicEventCard = memo(function MusicEventCard({
  event,
  compact = false,
  showArtifacts = true
}: MusicEventCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [playingArtifact, setPlayingArtifact] = useState<string | null>(null);

  // Get stage configuration - use event's stage or default to 'music'
  const eventStage = event.stage || 'music';
  const stageColors = getStageColors(eventStage);

  // Filter music artifacts
  const audioArtifacts = event.artifactRefs.filter(artifact => 
    artifact.type === 'audio'
  );

  // Get music metadata from event payload
  const musicMetadata = event.payload?.musicMetadata || {};
  const preset = musicMetadata.preset || event.payload?.preset;
  const durationRaw = event.payload?.duration || event.payload?.durationMs;
  
  // Standardize duration to seconds (handle both ms and s formats)
  const getDurationInSeconds = (dur: any): number | null => {
    if (!dur) return null;
    if (typeof dur === 'number') {
      // If greater than 3600, assume milliseconds; otherwise seconds
      return dur > 3600 ? Math.round(dur / 1000) : dur;
    }
    if (typeof dur === 'string') {
      const numVal = parseFloat(dur);
      if (dur.includes('ms')) return Math.round(numVal / 1000);
      if (dur.includes('s')) return Math.round(numVal);
      // If just a number string, assume milliseconds if > 3600
      return numVal > 3600 ? Math.round(numVal / 1000) : numVal;
    }
    return null;
  };
  
  const duration = getDurationInSeconds(durationRaw);

  const handlePlayToggle = (artifactId: string) => {
    setPlayingArtifact(current => current === artifactId ? null : artifactId);
  };

  return (
    <div 
      className="border-l-2"
      style={{
        borderLeftColor: stageColors.primaryColor,
        backgroundColor: 'var(--bg-surface)'
      }}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div 
            style={{
              marginTop: 'var(--space-1)',
              padding: 'var(--space-2)',
              backgroundColor: stageColors.bgSubtle,
              borderRadius: 'var(--radius-lg)'
            }}
          >
            <Music 
              style={{
                width: 'var(--space-5)',
                height: 'var(--space-5)',
                color: stageColors.textColor
              }}
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h4 
                  className="font-medium flex items-center"
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--fg-primary)',
                    gap: 'var(--space-2)'
                  }}
                >
                  {event.displayTitle}
                  {audioArtifacts.length > 0 && !compact && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="opacity-70 hover:opacity-100 transition-opacity"
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} audio artifacts`}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </h4>
                {event.displayDescription && (
                  <p 
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--fg-muted)',
                      marginTop: 'var(--space-1)'
                    }}
                  >
                    {event.displayDescription}
                  </p>
                )}
              </div>
              
              {/* Music Metadata */}
              <div 
                className="flex items-center"
                style={{
                  gap: 'var(--space-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--fg-muted)'
                }}
              >
                {preset && (
                  <span 
                    className="rounded"
                    style={{
                      padding: 'var(--space-1) var(--space-2)',
                      backgroundColor: stageColors.bgSubtle,
                      color: stageColors.textColor
                    }}
                  >
                    {preset}
                  </span>
                )}
                {duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {duration < 60 ? `${duration}s` : `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}`}
                  </span>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            {!compact && audioArtifacts.length > 0 && (
              <div 
                className="flex items-center"
                style={{
                  gap: 'var(--space-4)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--fg-muted)',
                  marginTop: 'var(--space-2)'
                }}
              >
                <span className="flex items-center gap-1">
                  <Volume2 className="w-3 h-3" />
                  {audioArtifacts.length} audio file{audioArtifacts.length !== 1 ? 's' : ''}
                </span>
                {event.correlation_id && (
                  <span className="flex items-center gap-1">
                    <Hash className="w-3 h-3" />
                    {event.correlation_id.slice(0, 8)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Audio Artifacts */}
        {showArtifacts && audioArtifacts.length > 0 && (isExpanded || compact) && (
          <div className="space-y-2">
            <h5 
              className="font-medium"
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--fg-muted)'
              }}
            >
              Audio Artifacts ({audioArtifacts.length})
            </h5>
            {audioArtifacts.map((artifact) => (
              <AudioArtifact
                key={artifact.id}
                artifact={artifact}
                isPlaying={playingArtifact === artifact.id}
                onPlayToggle={() => handlePlayToggle(artifact.id)}
              />
            ))}
          </div>
        )}

        {/* Compact Artifacts Preview */}
        {showArtifacts && audioArtifacts.length > 0 && !isExpanded && !compact && (
          <div className="mt-2">
            <div 
              className="flex items-center"
              style={{
                gap: 'var(--space-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--fg-muted)'
              }}
            >
              <span>Click to view {audioArtifacts.length} audio artifacts</span>
              {audioArtifacts.slice(0, 3).map(artifact => 
                artifact.audioType && (
                  <span 
                    key={artifact.id} 
                    className="rounded"
                    style={{
                      padding: 'var(--space-1) var(--space-2)',
                      backgroundColor: 'var(--bg-subtle)',
                      color: 'var(--fg-muted)'
                    }}
                  >
                    {artifact.audioType}
                  </span>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default MusicEventCard;